import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/formatDate';
import type { DebugSessionListItem } from '@/types/superUserDebug.ts';

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

interface Props {
  session: DebugSessionListItem;
}

function formatDuration(seconds: number | undefined, t: TFunction): string {
  if (seconds === undefined || seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return t('superuser.debug.detail.metadata.durationFormat', {
    defaultValue: '{{m}}m {{s}}s',
    m,
    s: String(s).padStart(2, '0'),
  });
}

export default function SessionMetadataCard({ session }: Props) {
  const { t } = useTranslation();

  const rows: Array<[string, React.ReactNode]> = [
    [t('superuser.debug.detail.metadata.id', 'Session ID'), <span className="font-mono text-xs">{session.id}</span>],
    [
      t('superuser.debug.detail.metadata.salesperson', 'Salesperson'),
      <span>
        {session.salespersonFullName || session.salespersonEmail}{' '}
        <span className="text-xs text-muted-foreground">({session.salespersonEmail})</span>
      </span>,
    ],
    [t('superuser.debug.detail.metadata.company', 'Compañía'), session.salespersonCompany || '—'],
    [t('superuser.debug.detail.metadata.simulation', 'Simulación'), session.simulationName ?? '—'],
    [t('superuser.debug.detail.metadata.simulationId', 'Simulation ID'), <span className="font-mono text-xs">{session.simulationId ?? '—'}</span>],
    [t('superuser.debug.detail.metadata.maestro', 'Maestro'), <span className="font-mono text-xs">{session.maestroId}</span>],
    [
      t('superuser.debug.detail.metadata.status', 'Estado'),
      <Badge variant="outline" className="font-mono text-[10px] uppercase">
        {t(`superuser.debug.status.${session.status}`, session.status)}
      </Badge>,
    ],
    [
      t('superuser.debug.detail.metadata.sessionType', 'Tipo'),
      <Badge variant="secondary" className="text-[10px] uppercase">
        {session.sessionType}
      </Badge>,
    ],
    [t('superuser.debug.detail.metadata.startedAt', 'Inicio'), <span className="font-mono text-xs">{formatDate(session.startedAt, DATE_OPTS)}</span>],
    [
      t('superuser.debug.detail.metadata.completedAt', 'Fin'),
      session.completedAt ? <span className="font-mono text-xs">{formatDate(session.completedAt, DATE_OPTS)}</span> : '—',
    ],
    [t('superuser.debug.detail.metadata.duration', 'Duración'), formatDuration(session.durationSeconds, t)],
  ];

  return (
    <div className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        {t('superuser.debug.detail.metadata.title', 'Datos de la sesión')}
      </h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {rows.map(([label, value], idx) => (
          <div key={idx} className="flex flex-col">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
            <dd className="break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
